import {EventEmitter, EventSubscriber, HasEventName} from "@/modules/base/events";
import {inject, injectable} from "inversify";
import {Player} from "@/modules/team/entity";
import {AfterCreateGameEvent} from "@/modules/base/events/AfterCreateGameEvent";
import {AfterDayEvaluationEvent} from "@/modules/season/events/AfterDayEvaluationEvent";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {Season} from "@/modules/season/entity";
import chance from "@/modules/base/services/chance";
import {BeforePlayerFormChangedEvent} from "@/modules/team/events/BeforePlayerFormChangedEvent";
import {AfterPlayerFormChangedEvent} from "@/modules/team/events/AfterPlayerFormChangedEvent";
import {AfterPlayerCreatedEvent} from "@/modules/team/events/AfterPlayerCreatedEvent";
import {WeekdayEnum} from "@/modules/base/enums";

@injectable()
export class PlayerFormEventSubscriber implements EventSubscriber {
  private readonly baseFormChangeRange: { min: number, max: number };
  private readonly formChangeRangeFromBaseForm: { min: number, max: number };
  private readonly formChangeDateRangeInDays: { min: number, max: number };

  constructor(
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(EventEmitter) private eventEmitter: EventEmitter,
  ) {
    this.baseFormChangeRange = this.configRepository.get('players.form.baseFormChangeRange');
    this.formChangeRangeFromBaseForm = this.configRepository.get('players.form.formChangeRangeFromBaseForm');
    this.formChangeDateRangeInDays = this.configRepository.get('players.form.formChangeDateRangeInDays');
  }

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: AfterDayEvaluationEvent, callback: this.handleFormChanges},
      {event: AfterDayEvaluationEvent, callback: this.handleBaseFormChanges},
      {event: AfterPlayerCreatedEvent, callback: this.onAfterPlayerCreatedEvent},
    ];
  }

  async onAfterPlayerCreatedEvent(event: AfterPlayerCreatedEvent) {
    // todo need this when season starts? random form
    if (this.configRepository.get('debug')) {
      const season = await Season.current();
      await this.handlePlayer(event.player, season);
    }
  }

  async handleFormChanges(event: AfterDayEvaluationEvent) {
    if (event.season.currentDate.weekday() !== WeekdayEnum.MONDAY) {
      return;
    }

    const season = await Season.current();
    const players = await Player
      .createQueryBuilder('player')
      .leftJoin('player.team', 'team')
      .where('player.careerEndedAt IS NULL')
      .andWhere('team.user = true')
      .getMany();

    await players.forEachAsync(async (player: Player) => this.handlePlayer(player, season, false));
  }

  async handleBaseFormChanges(event: AfterCreateGameEvent) {
    const season = await Season.current();
    const players = await Player
      .createQueryBuilder('player')
      .leftJoin('player.team', 'team')
      .where('player.nextFormChangeAt < :currentDate', {currentDate: season.currentDateRaw})
      .andWhere('team.user = true')
      .getMany();

    await players.forEachAsync(async (player: Player) => this.handlePlayer(player, season));
  }

  private async handlePlayer(player: Player, season: Season, changeBaseForm = true) {
    let newBaseForm = player.baseForm;

    if (changeBaseForm) {
      newBaseForm = Math.min(100, Math.max(1, player.baseForm + chance.integer(this.baseFormChangeRange)));
    }

    let nextFormChangeAt = player.nextFormChangeAt;

    if (!player.nextFormChangeAt || changeBaseForm) {
      nextFormChangeAt = season.currentDate.add(chance.integer(this.formChangeDateRangeInDays), 'days').toDate();
    }

    const newForm = Math.min(100, Math.max(1, newBaseForm + chance.integer(this.formChangeRangeFromBaseForm)));

    await this.eventEmitter.emitAsync(new BeforePlayerFormChangedEvent(player, newBaseForm, newForm, nextFormChangeAt!));

    player.baseForm = newBaseForm;
    player.form = newForm;
    player.nextFormChangeAt = nextFormChangeAt;
    await player.save();

    await this.eventEmitter.emitAsync(new AfterPlayerFormChangedEvent(player));
  }
}